import { useState, useEffect, useRef, useCallback } from "react"
import { useHistory } from "react-router-dom"
import { t } from "@lingui/macro"
import { ConfirmAlert } from "../components/Alerts"

export const useUnsavedChangesAlert = (
  hasUnsavedChanges,
  {
    alertTitle = t`Unsaved changes`,
    alertMessage = t`Are you sure you want to leave this page? Any unsaved changes will be lost.`,
    okButtonLabel = t`Discard changes`,
    cancelButtonLabel = t`Cancel`,
    okButtonProps = { color: "secondary" },
    onOk = () => {}
  } = {}
) => {
  const [showAlert, setShowAlert] = useState(false)
  const [pendingPath, setPendingPath] = useState(null)
  const history = useHistory()
  const unblockRef = useRef(null)

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    unblockRef.current = history.block((tx) => {
      setPendingPath(tx.pathname)
      setShowAlert(true)
      return false
    })

    return () => {
      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }
    }
  }, [hasUnsavedChanges, history])

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // Handle dialog response
  const handleConfirmNavigation = useCallback(
    (shouldProceed) => {
      setShowAlert(false)

      if (!shouldProceed || !pendingPath) {
        return
      }

      if (onOk) {
        onOk()
      }

      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }

      setTimeout(() => {
        history.push(pendingPath)
        setPendingPath(null)
      }, 0)
    },
    [history, pendingPath, onOk]
  )

  const UnsavedChangesAlert = useCallback(() => {
    if (!hasUnsavedChanges) {
      return null
    }

    return (
      <ConfirmAlert
        open={showAlert}
        title={alertTitle}
        description={alertMessage}
        okLabel={okButtonLabel}
        cancelLabel={cancelButtonLabel}
        okButtonProps={okButtonProps}
        onClose={(isOk) => handleConfirmNavigation(isOk)}
      />
    )
  }, [
    hasUnsavedChanges,
    showAlert,
    alertTitle,
    alertMessage,
    okButtonLabel,
    cancelButtonLabel,
    okButtonProps,
    handleConfirmNavigation
  ])

  return {
    UnsavedChangesAlert,
    isOpen: showAlert
  }
}
